/**
 * Homingo.
 *
 * Splash → onboarding → location → home, held in one piece of state.
 *
 * This is a stand-in for a navigator, not a design: it is enough while there
 * is a single path through the app, and swapping it for a real stack later
 * only touches this file.
 *
 * The opening stage is decided by what was stored last time, not by a constant.
 * Onboarding is shown once, the location flow only until an address exists, and
 * everything after that opens straight on Home - see `src/data/storage.ts`.
 *
 * There is no sign-in stage. Signing in happens inside Home, as a sheet at the
 * point it is actually needed (checkout), rather than as a wall in front of
 * the catalogue.
 *
 * ---------------------------------------------------------------------------
 * THE ONE THING THAT SITS ABOVE ALL FOUR STAGES
 * ---------------------------------------------------------------------------
 * `VoiceCallProvider` wraps the whole tree, and the two call overlays are its
 * siblings rather than anything's children. A call has to be able to interrupt
 * whatever is on screen - a stage here, one of `HomeScreen`'s two dozen
 * screens, a sheet over either - and with no navigator there is no stack to
 * push a call screen onto. Both overlays are React Native `Modal`s, which
 * float over the entire tree from wherever they are mounted, so mounting them
 * once here covers every one of those cases and leaves the stage machine below
 * exactly as it was.
 */

import { useCallback, useEffect, useState } from 'react';
import { StatusBar } from 'react-native';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { formatAddress, NO_ADDRESS, SavedAddress } from './src/data/places';
import {
  loadApp,
  RestoredApp,
  saveAddress,
  saveOnboarded,
} from './src/data/storage';
import { loadAppConfig } from './src/services/config';
import ForceUpdateGate from './src/components/update/ForceUpdateGate';
import OtaUpdateGate from './src/components/update/OtaUpdateGate';
import DialogHost from './src/components/ui/DialogHost';
import HomeScreen from './src/screens/HomeScreen';
import LocationScreen from './src/screens/LocationScreen';
import OnboardingScreen from './src/screens/OnboardingScreen';
import SplashScreen from './src/screens/SplashScreen';
import SlideIn from './src/components/ui/SlideIn';
import IncomingCallOverlay from './src/components/voice/IncomingCallOverlay';
import OngoingCallOverlay from './src/components/voice/OngoingCallOverlay';
import VoiceCallProvider from './src/voice/VoiceCallProvider';
import { colors } from './src/theme';

type Stage = 'splash' | 'onboarding' | 'location' | 'home';

function App() {
  const [stage, setStage] = useState<Stage>('splash');

  /**
   * What was on disk, or null while the read is still in flight.
   *
   * Nothing may render off this until it arrives - opening on Home with the
   * default empty address and then correcting it a frame later would flash a
   * signed-out header at someone who is signed in.
   */
  const [restored, setRestored] = useState<RestoredApp | null>(null);
  /** The splash has run its course. Independent of the read finishing. */
  const [splashDone, setSplashDone] = useState(false);
  /* `NO_ADDRESS` is both the prompt and the sentinel - the cart compares
     against it to decide whether a booking can proceed, which is why it is a
     shared constant rather than a string spelled out in each screen. */
  const [address, setAddress] = useState<string>(NO_ADDRESS);

  /**
   * The pin behind that line, when it is known.
   *
   * Kept beside the string rather than derived from the address book, because
   * the book is empty exactly when this matters most: during onboarding and
   * for anyone signed out. Without it the coverage check had no coordinates
   * to work from and stayed silent, so an address in a city we do not serve
   * looked identical to one in Indore.
   */
  const [addressPin, setAddressPin] = useState<
    { lat: number; lng: number } | undefined
  >(undefined);

  // Starts on mount, so the disk read overlaps the splash rather than
  // following it.
  useEffect(() => {
    loadApp().then(setRestored);
  }, []);

  /*
   * What this deployment can do, asked once while the splash is up.
   *
   * Nothing waits on it - it never rejects, and until it lands the app runs on
   * the cash-only fallback, which is the safe way to be wrong. Fired here
   * rather than at checkout so the answer is long since settled by the time
   * anyone reaches a basket.
   */
  useEffect(() => {
    loadAppConfig().catch(() => {
      /* `loadAppConfig` already falls back to cash-only on failure; there is
         nothing left for a caller to do with a rejection. */
    });
  }, []);

  /*
   * Leaves the splash once both the animation and the read are done.
   *
   * Whichever finishes second is the one that matters, which is why this is an
   * effect over two flags rather than a callback on either. Guarded on `stage`
   * so it cannot fire again after the app has moved on.
   */
  useEffect(() => {
    if (stage !== 'splash' || !splashDone || !restored) {
      return;
    }
    setAddress(restored.address);
    setAddressPin(restored.addressPin);
    setStage(
      !restored.seenOnboarding
        ? 'onboarding'
        : restored.address === NO_ADDRESS
        ? 'location'
        : 'home',
    );
  }, [restored, splashDone, stage]);

  const show = useCallback((next: Stage) => () => setStage(next), []);

  /** Every write of the address goes through here, so none can miss the disk. */
  const changeAddress = useCallback(
    (next: string, pin?: { lat: number; lng: number }) => {
      setAddress(next);
      setAddressPin(pin);
      saveAddress(next, pin);
    },
    [],
  );

  const onOnboarded = useCallback(() => {
    saveOnboarded();
    setStage('location');
  }, []);

  /**
   * The first address, before there is any account for a book to belong to.
   *
   * This is the ONLY place a picked address does not also try to save into
   * the real address book - `HomeScreen` handles every other case, because
   * that is where `signedIn` and the platform's book both live. Here there is
   * no session yet: sign-in happens later, "at the point it is actually
   * needed" per the file note above, so nothing exists yet to save this one
   * against. The line still has to go somewhere, which is what `changeAddress`
   * is for - it is honest local-only state until an account claims it.
   */
  const onAddressSaved = useCallback(
    (saved: SavedAddress) => {
      /* The pin travels with the line - see `addressPin`. This is the one
         path where the address book cannot supply it later, because there is
         no account yet for a book to belong to. */
      changeAddress(formatAddress(saved), saved.coords);
      setStage('home');
    },
    [changeAddress],
  );

  /**
   * The line belongs to the account, not the device.
   *
   * `address` is read from disk once, before anyone has signed in, and
   * nothing since scoped it to a phone number - so signing out and back in as
   * someone else on the same phone kept showing the previous account's
   * address. Called on sign-out, from `HomeScreen`, so a fresh sign-in always
   * starts from `NO_ADDRESS` instead of inheriting one. The address BOOK
   * needs no equivalent here - it lives behind `useAddresses` in `HomeScreen`
   * now, keyed on `signedIn` the same way `useBookings` already is, so it
   * clears itself.
   */
  const resetAddress = useCallback(() => {
    changeAddress(NO_ADDRESS);
  }, [changeAddress]);

  return (
    <SafeAreaProvider>
      <VoiceCallProvider>
        {/*
          Above everything, for the same reason the call overlays are: it is a
          `Modal`, so it floats over the whole tree from here - the splash, all
          four stages, every one of `HomeScreen`'s screens, and any sheet over
          any of them. It draws nothing at all unless the server says this
          build is too old, so the tree below is unaffected in the normal case.
        */}
        <ForceUpdateGate />

        {/*
          Its opposite number, and mounted here for the same reason: it is a
          `Modal`, and a staged JS update can become ready at any moment of any
          stage. Unlike the gate above it closes, because the app behind it
          works - see the long note in the file.

          `suppressed` is deliberately NOT wired. The thing it would guard
          against is a reload landing inside a Razorpay checkout, and nothing
          at this level knows that is happening: the payment screen is one of
          `HomeScreen`'s two dozen internal screens, and `payForBookings` is
          also run from the booking-recovery path there, so "a checkout is
          live" is state held several layers below this file with no signal
          reaching it. Surfacing it would mean threading a callback out of
          `HomeScreen` purely to feed this prop, which is more plumbing than
          the risk earns - the prompt only ever appears on a press, and
          "Update now" is the customer's own choice to make. If a payment
          context ever lands at the root, pass it here.
        */}
        <OtaUpdateGate />

        {/*
          Every confirmation and warning raised from outside a screen.

          Beside `ForceUpdateGate` and for the same reason: it needs nothing
          from the tree below, and the code that asks it questions runs in all
          of that tree's states - a catch handler in `AuthSheet`, a row action
          in the address book, a cancellation on the bookings tab. It draws
          nothing until something calls `confirm()` or `notify()` from
          `services/dialog`.
        */}
        <DialogHost />

        {/*
          `backgroundColor` is Android-only and ignored on iOS, where the status
          bar sits over the app and picks up the canvas behind it. Setting both
          keeps the bar readable on each platform.
        */}
        <StatusBar
          barStyle="dark-content"
          backgroundColor={colors.background}
        />

        {/* The splash is the first thing drawn, so there is nothing for it to
            slide in over. Every stage after it arrives from the right. */}
        {stage === 'splash' ? (
          <SplashScreen onDone={() => setSplashDone(true)} />
        ) : null}

        {stage === 'onboarding' ? (
          <SlideIn>
            <OnboardingScreen onDone={onOnboarded} />
          </SlideIn>
        ) : null}

        {stage === 'location' ? (
          <SlideIn>
            <LocationScreen
              onDone={onAddressSaved}
              onSkip={show('home')}
              /* Someone with an address already has answered the permission
                 question; sending them back to it to add a second one asks twice
                 and hides the map behind a screen they have seen. */
              initialStage={address === NO_ADDRESS ? 'permission' : 'fetching'}
              /*
               * Nothing can be saved here: the address book belongs to an
               * account and this step runs before anyone has signed in. Every
               * other way into this screen is behind `gated`, so this is the
               * only caller that says so - see the prop's own note.
               */
              canSave={restored?.session.signedIn ?? false}
            />
          </SlideIn>
        ) : null}

        {stage === 'home' ? (
          <SlideIn>
            <HomeScreen
              address={address}
              addressPin={addressPin}
              onChangeAddress={changeAddress}
              onSignedOut={resetAddress}
              /* Non-null by the time this renders: the stage only becomes
                 'home' inside the effect that waits for the read. */
              restored={restored}
            />
          </SlideIn>
        ) : null}

        {/*
          Last, and outside every stage. Each renders nothing at all until the
          provider's phase says otherwise, so this costs two mounted components
          and no pixels for the whole life of an app nobody calls.
        */}
        <IncomingCallOverlay />
        <OngoingCallOverlay />
      </VoiceCallProvider>
    </SafeAreaProvider>
  );
}

export default App;
